'use client';

import { useEffect, useRef, useState } from 'react';
import type { SceneStats } from '@config3d/scene';
import { applyPlayerTextureQualityPreset } from '@config3d/scene';
import type { AssetLoadStats } from '../lib/diagnostics-types';

interface PerfData {
  fps: number;
  frameTime: number;
  memory: number | null;
  loadTime: number | null;
  modelCount: number;
}

interface Props {
  modelCount: number;
  loadStartTime: number;
  allLoaded: boolean;
  sceneStats?: SceneStats | null;
  assetStats?: AssetLoadStats | null;
  textureQualityPreset?: string | null;
}

const TEX_PRESET_OPTIONS: { value: string; label: string }[] = [
  { value: 'full', label: 'Textures: full' },
  { value: '4k', label: 'Cap 4K' },
  { value: '2k', label: 'Cap 2K' },
  { value: '1k', label: 'Cap 1K' },
  { value: 'half', label: 'Scale 50%' },
  { value: 'quarter', label: 'Scale 25%' },
];

export function PerfOverlay({ modelCount, loadStartTime, allLoaded, sceneStats, assetStats, textureQualityPreset }: Props) {
  const [visible, setVisible] = useState(false);
  const [texPreset, setTexPreset] = useState(() => textureQualityPreset || 'full');
  const [perf, setPerf] = useState<PerfData>({
    fps: 0,
    frameTime: 0,
    memory: null,
    loadTime: null,
    modelCount: 0,
  });
  const framesRef = useRef<number[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!visible) return;

    let lastTime = performance.now();

    function tick() {
      const now = performance.now();
      const delta = now - lastTime;
      lastTime = now;

      framesRef.current.push(delta);
      if (framesRef.current.length > 60) framesRef.current.shift();

      const avgFrame = framesRef.current.reduce((a, b) => a + b, 0) / framesRef.current.length;
      const fps = Math.round(1000 / avgFrame);

      const mem = (performance as any).memory
        ? Math.round((performance as any).memory.usedJSHeapSize / 1048576)
        : null;

      setPerf({
        fps,
        frameTime: Math.round(avgFrame * 10) / 10,
        memory: mem,
        loadTime: allLoaded ? Math.round(Date.now() - loadStartTime) : null,
        modelCount,
      });

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible, modelCount, allLoaded, loadStartTime]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'p' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        setVisible((v) => !v);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    setTexPreset(textureQualityPreset || 'full');
  }, [textureQualityPreset]);

  if (!visible) return null;

  const fpsColor = perf.fps >= 55 ? 'text-green-400' : perf.fps >= 30 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="fixed top-3 left-3 z-50 bg-black/80 backdrop-blur-sm text-white rounded-lg px-3 py-2 font-mono text-[11px] space-y-0.5 min-w-[200px] max-h-[min(90vh,520px)] overflow-y-auto select-none">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] text-zinc-400 uppercase tracking-wider font-bold">Performance</span>
        <button
          onClick={() => setVisible(false)}
          className="text-zinc-500 hover:text-white text-xs leading-none"
        >
          ×
        </button>
      </div>
      <div className="flex justify-between">
        <span className="text-zinc-400">FPS</span>
        <span className={fpsColor}>{perf.fps}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-zinc-400">Frame</span>
        <span>{perf.frameTime}ms</span>
      </div>
      {perf.memory !== null && (
        <div className="flex justify-between">
          <span className="text-zinc-400">JS heap</span>
          <span>{perf.memory}MB</span>
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-zinc-400">Models</span>
        <span>{perf.modelCount}</span>
      </div>
      {perf.loadTime !== null && (
        <div className="flex justify-between">
          <span className="text-zinc-400">Load (TTI)</span>
          <span>{perf.loadTime > 1000 ? `${(perf.loadTime / 1000).toFixed(1)}s` : `${perf.loadTime}ms`}</span>
        </div>
      )}

      <div className="border-t border-white/10 pt-1 mt-1">
        <label className="block text-[9px] text-zinc-500 uppercase mb-0.5">Texture quality</label>
        <select
          className="w-full bg-zinc-800 text-white text-[11px] rounded px-1.5 py-1 border border-zinc-600"
          value={texPreset}
          onChange={(e) => {
            const v = e.target.value;
            setTexPreset(v);
            applyPlayerTextureQualityPreset(v);
          }}
        >
          {TEX_PRESET_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <p className="text-[8px] text-zinc-500 mt-0.5 leading-tight">Also: URL <code className="text-zinc-400">?tex=2k</code> (full, 4k, 2k, 1k, half, quarter)</p>
      </div>

      {assetStats && (
        <>
          <div className="border-t border-white/10 pt-1 mt-1 text-[9px] text-zinc-500 uppercase">Assets</div>
          <div className="flex justify-between gap-2">
            <span className="text-zinc-400 shrink-0">Scene objs</span>
            <span className="truncate text-right">{assetStats.sceneObjectCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Rendered</span>
            <span>{assetStats.sceneModelsRendered}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">GLB catalog</span>
            <span>{assetStats.catalogGlbCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Lib materials</span>
            <span>{assetStats.libraryMaterialCount}</span>
          </div>
        </>
      )}

      {sceneStats && (
        <>
          <div className="border-t border-white/10 pt-1 mt-1 text-[9px] text-zinc-500 uppercase">GPU / scene</div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Δ scene / GPU</span>
            <span>{sceneStats.triangles.toLocaleString()} / {sceneStats.renderTriangles.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Draw calls</span>
            <span>{sceneStats.drawCalls}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Meshes / mats</span>
            <span>{sceneStats.meshes} / {sceneStats.materials}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Tex / geo</span>
            <span>{sceneStats.textures} / {sceneStats.geometries}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Programs</span>
            <span>{sceneStats.programs}</span>
          </div>
        </>
      )}

      <div className="border-t border-white/10 pt-1 mt-1">
        <div className="w-full bg-zinc-700 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all ${perf.fps >= 55 ? 'bg-green-400' : perf.fps >= 30 ? 'bg-yellow-400' : 'bg-red-400'}`}
            style={{ width: `${Math.min(100, (perf.fps / 60) * 100)}%` }}
          />
        </div>
      </div>
      <div className="text-[9px] text-zinc-500 text-center pt-0.5">Ctrl+Shift+P to toggle</div>
    </div>
  );
}
